import { toast } from '@estac1/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api.js';
import type { AuditEntry, DriftResponse, PricingSettings, RecalcResponse } from './types.js';

export const usePricingSettingsQueries = (onSaveSuccess?: () => void) => {
  const qc = useQueryClient();

  const settings = useQuery({
    queryKey: ['admin-settings-pricing'],
    queryFn: () => api.request<PricingSettings>('/v1/admin/settings/pricing'),
  });

  const audit = useQuery({
    queryKey: ['admin-audit', 'platform_settings'],
    queryFn: () =>
      api.request<{ entries: AuditEntry[] }>('/v1/admin/audit', { query: { limit: 200 } }),
  });

  const drift = useQuery({
    queryKey: ['admin-pricing-drift'],
    queryFn: () => api.request<DriftResponse>('/v1/admin/billing/pricing-drift'),
  });

  const recalc = useMutation({
    mutationFn: () =>
      api.request<RecalcResponse>('/v1/admin/billing/pricing-drift/recalculate', {
        method: 'POST',
      }),
    onSuccess: (result) => {
      const parts = [`${result.updated} atualizadas`];
      if (result.failed > 0) parts.push(`${result.failed} com falha`);
      toast.success(`Recalculo concluído: ${parts.join(', ')}.`);
      qc.invalidateQueries({ queryKey: ['admin-pricing-drift'] });
      qc.invalidateQueries({ queryKey: ['admin-audit', 'platform_settings'] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : 'Falha ao recalcular.';
      toast.error(msg);
    },
  });

  const save = useMutation({
    mutationFn: (input: PricingSettings) =>
      api.request<{ ok: true }>('/v1/admin/settings/pricing', {
        method: 'PUT',
        json: {
          ...input,
          tiers: input.tiers.map((t) => ({
            minVacancies: t.minVacancies,
            maxVacancies: t.maxVacancies,
            monthlyCents: t.monthlyCents,
          })),
        },
      }),
    onSuccess: () => {
      onSaveSuccess?.();
      toast.success('Preços atualizados. Propagação em até 5 minutos.');
      qc.invalidateQueries({ queryKey: ['admin-settings-pricing'] });
      qc.invalidateQueries({ queryKey: ['admin-audit', 'platform_settings'] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : 'Falha ao salvar.';
      toast.error(msg);
    },
  });

  return { settings, audit, drift, recalc, save };
};
